import { format, isDefined, ISoilAnalysis, ISoilInterpretation, isUndefined, soilColors } from "../../omnia"
import { IInterpretationProperty, ISoilService } from "../../types"

const DefaultComment = "There is no interpretation for this value"

const gaugeProperties = ["P2O5", "K2O", "MgO", "WaterpH", "ActiveCarbonIndex", "AvailableP2O5", "AvailableK2O", "AvailableMgO", "AvailableCaO", "AvailableSO3", "AvailableNa"];

export function getInterpretations(svc: ISoilService, analysis: ISoilAnalysis) {

    const definitions = svc.getInterpretations()

    const groups = definitions.map(g => <InterpGroup>({
        Name: g.Name,
        Index: g.Index,
        Properties: g.Properties.map(p => getProperty(p, analysis))
    }))

    return groups

}

function getProperty(def: IInterpretationProperty, analysis: ISoilAnalysis) {

    const interpretation = analysis.Interpretations
        ? analysis.Interpretations.find(n => n.PropertyName === def.PropertyName)
        : null

    return new InterpProp(def, analysis, interpretation)

}

export interface InterpGroup {

    Name: string,
    Index: number,
    Properties: InterpProp[]

}

export class InterpProp {

    public readonly labelHtml: string

    private _currentValue: any
    private _defaultText: string

    private _text: string

    constructor(private def: IInterpretationProperty, private analysis: ISoilAnalysis, private prop: ISoilInterpretation) {
        this._text = prop ? prop.Comment : undefined
        this.labelHtml = format.formatChemicalCompoundInTextAsHtml(def.Label)
    }

    get name() {
        return this.def.PropertyName
    }

    get unit() {
        return this.def.Unit
    }

    get text() {
        return isUndefined(this._text) ? this.getDefaultComment() : this._text
    }

    set text(value) {
        this._text = value ? value : ""
    }

    get SoilInterpretation() {
        return this.prop
    }

    private showGauge(): boolean {
        return (gaugeProperties.indexOf(this.name) !== -1) && this.showInterpretationValue()
    }

    private showInterpretationValue(): boolean {
        return this.analysis[this.name] !== undefined;
    }

    private getInterpretationValue() {
        return this.analysis[this.name];
    }

    private getNormalizedInterpretationValue() {

        let value = this.getInterpretationValue();

        if (this.def.Unit === "%") {
            value = Math.round(value * 100 * 100) / 100;
        }

        return value;
    }

    private getGaugeStyle() {
        const nutrient = this.name
        const value = this.getInterpretationValue()
        return soilColors.getGaugeStyle(value, nutrient === 'WaterpH' ? 'pH' : nutrient);
    }

    getDefaultComment(defaultText: string = DefaultComment) {

        const value = this.getInterpretationValue()
        let text = undefined

        if ((!isDefined(this._currentValue)) || (value !== this._currentValue)) {

            const values = this.def.Values

            const matches = values.filter(v => v.Min <= value)

            const interpretation = matches.length
                ? matches[matches.length - 1]
                : values[0]

            text = this._defaultText = interpretation ? interpretation.Comment : undefined


        }

        return text || defaultText;

    }

    usesDefaultComment() {
        return (!this._text) || (this._text == this.getDefaultComment())
    }

    getUpdateModel(): ISoilInterpretation {

        const i = this.prop || {
            SoilInterpretationId: 0,
            SoilAnalysisId: this.analysis.SoilAnalysisId,
            PropertyName: this.def.PropertyName,
            Comment: this._text
        }

        i.Comment = this.usesDefaultComment() ? null : this._text

        return (i.Comment || i.SoilInterpretationId) ? i : null

    }

    hasDefaultComment() {
        return !!this.getDefaultComment("")
    }

    resetComment() {
        this._text = this.getDefaultComment("") || this._text
    }

}
